import { useParams } from 'react-router-dom';
import { useAuth } from '../../shared/context';
import { useDataRoomQuery, useDataRoomSummaryQuery, useLenderSummaryReportQuery } from '../../shared/hooks';
import {
	CerebroBreadcrumb,
	CerebroIcon,
	CerebroLink,
	CerebroPageContainer,
	CerebroText,
	CerebroTextButton,
} from '../../shared/components';
import { Grid, Skeleton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { NumberUtils } from '../../shared/utils';
import { LenderFeedbackSummary } from '../../shared/models';
import React from 'react';
import { LenderSummaryReportForm } from './LenderSummaryReportForm';
import { sanitize } from 'dompurify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEyeSlash } from '@fortawesome/pro-light-svg-icons';
import { CerebroApiEndpoints } from '../../shared/utils/api';

export const LenderSummaryReport: React.FC = () => {
	const { guid } = useParams<'guid'>();
	const { currentUser } = useAuth();
	const dataRoomQuery = useDataRoomQuery(guid);
	const reportQuery = useLenderSummaryReportQuery(guid, {
		enabled: dataRoomQuery.isSuccess,
		retry: false,
	});

	const summaryQuery = useDataRoomSummaryQuery(guid);
	
	const isLoading = reportQuery.isLoading || reportQuery.isFetching;
	const [editReport, setEditReport] = React.useState<LenderFeedbackSummary | null>(null);
	const inEditMode =
		reportQuery.isSuccess &&
		dataRoomQuery.isSuccess &&
		dataRoomQuery.data.isEditingFeedbackSummary &&
		currentUser.isEmployeeUser;
	const colSpanLength = inEditMode ? 4 : 3;

	const onCloseForm = () => {
		setEditReport(null);
	};

	return (
		<CerebroPageContainer maxWidth="100%">
			<CerebroBreadcrumb>Lender Progress &gt; Feedback Summary Report</CerebroBreadcrumb>

			{isLoading && (
				<>
					<Skeleton height={60} />
					<Skeleton height={60} />
					<Skeleton height={60} />
					<Skeleton height={60} />
				</>
			)}

			{reportQuery.isSuccess && (
				<>
					<Grid container>
						<Grid item xs={1}></Grid>
						<Grid item xs={2} sx={{ marginBottom: 2 }}>
							<CerebroText weight="bold">Loan Request Size</CerebroText>
						</Grid>
						<Grid item xs={3}>
							{NumberUtils.FormatCurrency(reportQuery.data.header.totalRequestedAmount)}
						</Grid>
						<Grid item xs={2}>
							<CerebroText weight="bold">Lenders Accepted</CerebroText>
						</Grid>
						<Grid item xs={3}>
							{summaryQuery.data?.institutionLendersAccepted.toString()}
						</Grid>
						<Grid item xs={1}></Grid>

						<Grid item xs={1}></Grid>
						<Grid item xs={2} sx={{ marginBottom: 2 }}>
							<CerebroText weight="bold">Loan Purpose</CerebroText>
						</Grid>
						<Grid item xs={3}>
							{reportQuery.data.header.loanPurpose}
						</Grid>
						<Grid item xs={2}>
							<CerebroText weight="bold">Commercial Banks</CerebroText>
						</Grid>
						<Grid item xs={3}>
							{reportQuery.data.header.commercialBanks}
						</Grid>
						<Grid item xs={1}></Grid>

						<Grid item xs={1}></Grid>
						<Grid item xs={2}>
							<CerebroText weight="bold">Loan Type</CerebroText>
						</Grid>
						<Grid item xs={3}>
							{reportQuery.data.header.loanType}
						</Grid>
						<Grid item xs={2}>
							<CerebroText weight="bold">Non-Banks</CerebroText>
						</Grid>
						<Grid item xs={3}>
							{reportQuery.data.header.nonBanks}
						</Grid>
						<Grid item xs={1}></Grid>
					</Grid>

					<TableContainer sx={{ marginTop: 3 }}>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell>Lender Institution</TableCell>
									<TableCell>Feedback</TableCell>
									<TableCell>Re-Engage Point</TableCell>
									{inEditMode && <TableCell></TableCell>}
								</TableRow>
							</TableHead>
							<TableBody>
								{reportQuery.data.lenderSummaries
									.filter(summary => summary.isVisibleToBorrower || inEditMode)
									.map(summary => (
										<TableRow key={summary.bankDealProfileId}>
											<TableCell>
												{inEditMode && !summary.isVisibleToBorrower && (
													<CerebroIcon
														padRight
														sx={theme => ({ color: theme.palette.success.main })}
														title="Hidden to borrower"
													>
														<FontAwesomeIcon icon={faEyeSlash} size="sm" />
													</CerebroIcon>
												)}
												{summary.bankName}
											</TableCell>
											<TableCell>
												<div
													dangerouslySetInnerHTML={{
														__html: sanitize(summary?.feedbackTextDisplay ?? '<p></p>'),
													}}
												></div>
											</TableCell>
											<TableCell>
												<div
													dangerouslySetInnerHTML={{
														__html: sanitize(summary?.nextStepsTextDisplay ?? '<p></p>'),
													}}
												></div>
											</TableCell>
											{inEditMode && (
												<TableCell>
													<CerebroTextButton onClick={() => setEditReport(summary)}>
														Edit
													</CerebroTextButton>
												</TableCell>
											)}
										</TableRow>
									))}
								{reportQuery.data.lenderSummaries.filter(
									summary => summary.isVisibleToBorrower || inEditMode
								).length == 0 && (
									<TableRow>
										<TableCell colSpan={colSpanLength} sx={{ textAlign: 'center' }}>
											<CerebroText sx={{ paddingTop: 3, paddingBottom: 3 }}>
												There are currently no lenders that have opted out of this data room.
											</CerebroText>
										</TableCell>
									</TableRow>
								)}
							</TableBody>
						</Table>
					</TableContainer>

					<br />
					<CerebroLink
						href={CerebroApiEndpoints.DataRooms.DownloadLenderSummaryReport(guid)}
						target="_blank"
						rel="noreferrer"
					>
						Download Report
					</CerebroLink>
				</>
			)}

			<LenderSummaryReportForm dataRoomGuid={guid} report={editReport} close={onCloseForm} />
		</CerebroPageContainer>
	);
};
